<script setup lang="ts">
import { PATHWAY_EVENT_STEP_COST_TYPE, type Step } from '@/types/shared/pathway-event'
import { computed } from 'vue'
import { usePlayer } from '@/composables/game/player'
import { useClientDeals } from '@/composables/client-deals'
import CheckIconThin from '@/components/icons/CheckIconThin.vue'
import LockIcon from '@/components/icons/LockIcon.vue'
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue'
import { formatUnshortenedNumber } from '@/helpers/utils'

const props = withDefaults(defineProps<{
	eventId: string
	step: Step
	isDone?: boolean
	isClaimable?: boolean
	index: number
	isLoading?: boolean
	isLastStep?: boolean
}>(), {
  isDone: false,
  isClaimable: false,
  isLoading: false,
  isLastStep: false,
})

const emit = defineEmits<{
	(e: 'onPurchase'): void
	(e: 'onPay'): void
	(e: 'outOfSpecialCurrency', currencyId: string): void
}>()

const { goldenCoinValue, player } = usePlayer()
const { rewardImageMap, getCurrentStepConfig, getVisibleRewards, getRewardAmount } = useClientDeals()

const playerSpecialCurrencies = computed(() => player.value.specialCurrencies)

const isLocked = computed(() => !props.isDone && !props.isClaimable)

const currentStepConfig = computed(() => getCurrentStepConfig(props.index))

const visibleRewards = computed(() => getVisibleRewards(props.step))

const outerContainerClass = computed(() => {
  if (props.isLastStep) {
    return 'bg-[linear-gradient(180deg,#FFF246_0%,#E5B80B_37%,#FFF571_69%,#E5B80B_100%)]'
  }
  if (props.isDone) {
    return 'bg-[#14F1FF]/[.44]'
  }
  return 'bg-[linear-gradient(180deg,#BBAACC_0%,rgba(251,231,198,0.66)_55%,rgba(208,169,245,0)_89%)]'
})

const innerContainerClass = computed(() => {
  if (props.isLastStep) {
    return 'bg-[linear-gradient(180deg,#FAD961_0%,rgba(247,107,28,0.66)_55%,rgba(211,131,18,0)_89%)]'
  }
  return 'bg-dark-bottom-layer'
})

const costBgClass = computed(() => {
  if (props.isLastStep || props.isClaimable) {
    return 'bg-white'
  }
  if (props.isDone) {
    return 'bg-dark-most-bottom-layer'
  }
  return 'bg-dark-top-layer'
})

const stepCostLabel = computed(() => {
  if (props.step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.FREE) {
    return 'FREE'
  }
  if (props.step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.STAR) {
    return `$${props.step.cost.usdAmount?.toFixed(2)}`
  }
  return formatUnshortenedNumber(props.step.cost.amount)
})

async function onStepClick() {
  if (!props.isClaimable || !currentStepConfig.value) return
  if (props.step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.STAR) {
    emit('onPurchase')
    return
  }
  if (props.step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.SPECIAL_CURRENCY && props.step.cost.amount > (playerSpecialCurrencies.value[props.step.cost.currencyId] ?? 0)) {
    emit('outOfSpecialCurrency', props.step.cost.currencyId)
    return
  }
  emit('onPay')
}
</script>

<template>
  <div
    class="w-full p-0.5 rounded-2xl flex"
    :class="[outerContainerClass, {
      'opacity-[.66]': isLocked,
      'pointer-events-none': !isClaimable || isLoading,
      'pulse-claimable claimable-shadow': !isDone && isClaimable,
    }]"
    @click="onStepClick"
  >
    <div class="w-full h-full flex rounded-2xl bg-dark-bottom-layer">
      <div
        class="w-full h-full p-3 flex flex-col gap-y-2 rounded-2xl"
        :class="innerContainerClass"
      >
        <div class="flex flex-col items-center">
          <img
            class="size-[44px] min-w-[44px] object-cover"
            :src="currentStepConfig.image"
          >
          <div class="black-stroke-white-text-2 font-bold text-base text-center">
            {{ currentStepConfig.title }}
          </div>
          <div class="text-center text-sm font-semibold text-white/[.60]">
            {{ currentStepConfig.subtitle }}
          </div>
        </div>

        <div
          class="flex items-center justify-center gap-x-[14px]"
        >
          <div
            v-for="(rewardType, _index) in visibleRewards"
            :key="_index"
            class="flex items-center gap-x-1"
          >
            <img
              class="h-auto object-cover"
              :class="isLastStep ? 'w-5' : 'w-4'"
              :src="rewardImageMap[rewardType as keyof typeof rewardImageMap]"
            >
            <span
              class="text-white font-bold"
              :class="isLastStep ? 'text-sm' : 'text-[17px] leading-6'"
            >{{ getRewardAmount(rewardType, step, goldenCoinValue) }}</span>
          </div>
        </div>

        <div
          class="w-full h-8 min-h-[32px] rounded-[6px] transition-colors flex items-center justify-center"
          :class="costBgClass"
        >
          <CheckIconThin
            v-if="isDone"
            class="size-8 text-teal-primary"
          />
          <div
            v-else
            class="flex items-center gap-x-1"
          >
            <SpinnerIcon
              v-if="isLoading"
              class="size-4 min-w-[16px] text-black/[.66]"
            />
            <template v-else>
              <LockIcon
                v-if="isLocked"
                class="size-4 min-w-[16px]"
                :class="isLastStep ? 'text-black/[.66]' : 'text-white/[.66]'"
              />
              <img
                v-if="step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.SPECIAL_CURRENCY"
                class="h-4 w-auto image-shadow object-cover"
                :src="rewardImageMap[step.cost.currencyId]"
              >
              <span class="black-stroke-white-text-3 text-base font-bold uppercase">{{ stepCostLabel }}</span>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shadow-container {
	box-shadow: 0px 0px 20px 0px #000;
}

.gold-background {
	background: linear-gradient(
		to right,
		#fff246 0%,
		#e5b80b 37%,
		#fff571 69%,
		#e5b80b 100%
	);
}

.claimable-background {
	background: linear-gradient(
		-45deg,
		rgba(237, 255, 202, 0.22) 13%,
			/* #EDFFCA at 22% opacity */ rgba(215, 115, 215, 1) 24%,
			/* #48650C at 100% opacity */ rgba(20, 241, 255, 1) 42%,
			/* #14F1FF at 100% opacity */ rgba(20, 241, 255, 1) 63%,
			/* #14F1FF at 100% opacity (same color) */ rgba(254, 78, 254, 0.77) 79%,
			/* #EEFFCA at 77% opacity */ rgba(215, 115, 215, 1) 82% /* #E2FFA7 at 100% opacity */
	);
}

.black-stroke {
	text-shadow: -1px -1px 0 #000000,
	1px -1px 0 #000000,
	-1px 1px 0 #000000,
	1px 1px 0 #000000,
	-1.5px 0 0 #000000,
	1.5px 0 0 #000000,
	0 -1.5px 0 #000000,
	0 1.5px 0 #000000,
	-0.5px -0.5px 0 #000000, /* Finer shadow adjustments */ 0.5px -0.5px 0 #000000,
	-0.5px 0.5px 0 #000000,
	0.5px 0.5px 0 #000000;
}

.image-shadow {
	filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.66));
}

.claimable-shadow {
	filter: drop-shadow(0px 0px 12px rgba(20, 241, 255, 0.88)); /* Full shadow */
}

.claimable-shadow-visual {
	filter: drop-shadow(0px 0px 3px rgba(20, 241, 255, 0.88)); /* Full shadow */
}

.pulse-claimable {
	animation: pulse-step 1s infinite linear;
	will-change: transform;
}

@keyframes pulse-step {
	0% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.04);
	}
	100% {
		transform: scale(1);
	}
}
</style>